import { Section, CodeBlock, InfoBox } from "@/components"

export function ReactShadcnThemingPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Theming & Customization</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Learn how to customize the look and feel of your application. The template uses CSS 
          variables for theming, making it easy to create custom color schemes and dark mode.
        </p>
      </div>

      <Section title="Theme System" description="How theming works">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template uses CSS variables defined in your global styles. Shadcn UI components 
            automatically adapt to these variables, providing consistent theming across your app.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">CSS Variables</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                All colors and design tokens are defined as CSS custom properties in 
                <code className="bg-muted px-2 py-1 rounded mx-1">globals.css</code>
              </p>
            </div>
            <div className="bg-card border rounded-lg p-6 space-y-3">
              <h3 className="font-semibold">Dark Mode</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Automatic dark mode support with separate variable definitions and system 
                preference detection built-in.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Color System" description="Theme variables">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Theme colors are defined in <code className="bg-muted px-2 py-1 rounded">src/index.css</code> using HSL values for better control.
          </p>
          
          <CodeBlock
            code={`@layer base {
  :root {
    /* Background colors */
    --background: 0 0% 100%;
    --foreground: 240 10% 3.9%;

    /* Card colors */
    --card: 0 0% 100%;
    --card-foreground: 240 10% 3.9%;

    /* Popover colors */
    --popover: 0 0% 100%;
    --popover-foreground: 240 10% 3.9%;

    /* Primary brand color */
    --primary: 240 5.9% 10%;
    --primary-foreground: 0 0% 98%;

    /* Secondary color */
    --secondary: 240 4.8% 95.9%;
    --secondary-foreground: 240 5.9% 10%;

    /* Muted colors for less prominent elements */
    --muted: 240 4.8% 95.9%;
    --muted-foreground: 240 3.8% 46.1%;

    /* Accent color for highlights */
    --accent: 240 4.8% 95.9%;
    --accent-foreground: 240 5.9% 10%;

    /* Destructive color for errors/deletions */
    --destructive: 0 84.2% 60.2%;
    --destructive-foreground: 0 0% 98%;

    /* Border and input colors */
    --border: 240 5.9% 90%;
    --input: 240 5.9% 90%;
    --ring: 240 5.9% 10%;

    /* Border radius */
    --radius: 0.5rem;
  }

  .dark {
    /* Dark mode background colors */
    --background: 240 10% 3.9%;
    --foreground: 0 0% 98%;

    /* Dark mode card colors */
    --card: 240 10% 3.9%;
    --card-foreground: 0 0% 98%;

    /* Dark mode popover colors */
    --popover: 240 10% 3.9%;
    --popover-foreground: 0 0% 98%;

    /* Dark mode primary color */
    --primary: 0 0% 98%;
    --primary-foreground: 240 5.9% 10%;

    /* Dark mode secondary color */
    --secondary: 240 3.7% 15.9%;
    --secondary-foreground: 0 0% 98%;

    /* Dark mode muted colors */
    --muted: 240 3.7% 15.9%;
    --muted-foreground: 240 5% 64.9%;

    /* Dark mode accent color */
    --accent: 240 3.7% 15.9%;
    --accent-foreground: 0 0% 98%;

    /* Dark mode destructive color */
    --destructive: 0 62.8% 30.6%;
    --destructive-foreground: 0 0% 98%;

    /* Dark mode border and input colors */
    --border: 240 3.7% 15.9%;
    --input: 240 3.7% 15.9%;
    --ring: 240 4.9% 83.9%;
  }
}`}
            language="css"
            filename="src/index.css"
          />

          <InfoBox type="info" title="HSL Color Format">
            Colors use HSL (Hue, Saturation, Lightness) format without the <code className="bg-muted px-2 py-1 rounded">hsl()</code> wrapper. 
            This allows Tailwind to apply opacity modifiers like <code className="bg-muted px-2 py-1 rounded">bg-primary/50</code>.
          </InfoBox>
        </div>
      </Section>

      <Section title="Customizing Colors" description="Create your own theme">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            To customize your theme, simply update the CSS variables in your <code className="bg-muted px-2 py-1 rounded">index.css</code> file.
          </p>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Example: Blue Theme</h3>
            <CodeBlock
              code={`:root {
  --background: 0 0% 100%;
  --foreground: 222.2 84% 4.9%;

  --primary: 221.2 83.2% 53.3%;        /* Blue primary color */
  --primary-foreground: 210 40% 98%;

  --secondary: 210 40% 96.1%;
  --secondary-foreground: 222.2 47.4% 11.2%;

  --accent: 210 40% 96.1%;
  --accent-foreground: 222.2 47.4% 11.2%;

  --destructive: 0 84.2% 60.2%;
  --destructive-foreground: 210 40% 98%;

  --border: 214.3 31.8% 91.4%;
  --input: 214.3 31.8% 91.4%;
  --ring: 221.2 83.2% 53.3%;           /* Match primary */

  --radius: 0.5rem;
}`}
              language="css"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Example: Purple Theme</h3>
            <CodeBlock
              code={`:root {
  --primary: 262.1 83.3% 57.8%;        /* Purple primary color */
  --primary-foreground: 210 20% 98%;
  
  --ring: 262.1 83.3% 57.8%;           /* Match primary */
  
  /* Keep other variables the same or adjust as needed */
}`}
              language="css"
            />
          </div>
        </div>
      </Section>

      <Section title="Dark Mode Implementation" description="Toggle between themes">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template includes a complete dark mode implementation with system preference detection.
          </p>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Theme Store</h3>
            <p className="text-sm text-muted-foreground">
              The <code className="bg-muted px-2 py-1 rounded">useThemeStore</code> handles theme switching:
            </p>
            <CodeBlock
              code={`import { useThemeStore } from "@/store/theme.store"

function App() {
  const { theme, setTheme } = useThemeStore()

  // Set theme (options: "light", "dark", "system")
  setTheme("dark")

  return <div>...</div>
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Theme Toggle Component</h3>
            <CodeBlock
              code={`import { Moon, Sun, Monitor } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useThemeStore } from "@/store/theme.store"

export function ThemeToggle() {
  const { setTheme } = useThemeStore()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme("light")}>
          <Sun className="mr-2 h-4 w-4" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")}>
          <Moon className="mr-2 h-4 w-4" />
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")}>
          <Monitor className="mr-2 h-4 w-4" />
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Using Theme Colors" description="Apply colors in components">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Use Tailwind classes that reference the CSS variables to apply theme colors.
          </p>
          
          <CodeBlock
            code={`// Background colors
<div className="bg-background">...</div>
<div className="bg-card">...</div>
<div className="bg-muted">...</div>

// Text colors
<p className="text-foreground">...</p>
<p className="text-muted-foreground">...</p>
<p className="text-primary">...</p>

// Border colors
<div className="border border-border">...</div>
<div className="border-primary">...</div>

// With opacity modifiers
<div className="bg-primary/50">50% opacity</div>
<div className="bg-muted/30">30% opacity</div>

// Hover states
<button className="hover:bg-accent hover:text-accent-foreground">
  Hover me
</button>`}
            language="typescript"
          />
        </div>
      </Section>

      <Section title="Component Variants" description="Shadcn UI styling">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Shadcn UI components come with built-in variants that use your theme colors.
          </p>
          
          <CodeBlock
            code={`import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"

function Example() {
  return (
    <>
      {/* Button variants */}
      <Button variant="default">Default</Button>
      <Button variant="secondary">Secondary</Button>
      <Button variant="destructive">Destructive</Button>
      <Button variant="outline">Outline</Button>
      <Button variant="ghost">Ghost</Button>
      <Button variant="link">Link</Button>

      {/* Badge variants */}
      <Badge variant="default">Default</Badge>
      <Badge variant="secondary">Secondary</Badge>
      <Badge variant="destructive">Destructive</Badge>
      <Badge variant="outline">Outline</Badge>

      {/* Alert variants */}
      <Alert>
        <AlertDescription>Default alert</AlertDescription>
      </Alert>
      <Alert variant="destructive">
        <AlertDescription>Error alert</AlertDescription>
      </Alert>
    </>
  )
}`}
            language="typescript"
          />
        </div>
      </Section>

      <Section title="Border Radius" description="Customize component roundness">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Adjust the global border radius by changing the <code className="bg-muted px-2 py-1 rounded">--radius</code> variable.
          </p>
          
          <CodeBlock
            code={`:root {
  --radius: 0.5rem;  /* Default - Medium rounded */
}

/* More rounded */
:root {
  --radius: 0.75rem;
}

/* Sharp corners */
:root {
  --radius: 0rem;
}

/* Very rounded */
:root {
  --radius: 1rem;
}`}
            language="css"
          />

          <InfoBox type="info" title="Consistent Roundness">
            Changing <code className="bg-muted px-2 py-1 rounded">--radius</code> updates all components 
            automatically, maintaining visual consistency across your app.
          </InfoBox>
        </div>
      </Section>

      <Section title="Custom Theme Generator" description="Generate themes easily">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Use the Shadcn UI theme generator to create custom themes visually:
          </p>
          
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="font-semibold">Steps:</h3>
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="text-primary shrink-0">1.</span>
                <span>Visit <a href="https://ui.shadcn.com/themes" target="_blank" rel="noopener noreferrer" className="text-primary underline">ui.shadcn.com/themes</a></span>
              </li>
              <li className="flex gap-3">
                <span className="text-primary shrink-0">2.</span>
                <span>Customize colors using the visual color picker</span>
              </li>
              <li className="flex gap-3">
                <span className="text-primary shrink-0">3.</span>
                <span>Copy the generated CSS variables</span>
              </li>
              <li className="flex gap-3">
                <span className="text-primary shrink-0">4.</span>
                <span>Paste into your <code className="bg-muted px-2 py-1 rounded">index.css</code> file</span>
              </li>
              <li className="flex gap-3">
                <span className="text-primary shrink-0">5.</span>
                <span>See changes instantly in your app</span>
              </li>
            </ol>
          </div>
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Theming Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use semantic color names (primary, secondary) rather than specific colors (blue, red)</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Test your theme in both light and dark modes</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Maintain sufficient color contrast for accessibility (4.5:1 ratio minimum)</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep border radius consistent by using the --radius variable</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use opacity modifiers (bg-primary/50) for subtle variations</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Document your custom colors if sharing the codebase with a team</span>
          </li>
        </ul>
      </div>
    </div>
  )
}
